import type {
  SidebarNavGroupChild,
  SidebarNavGroupItem,
  SidebarNavItem,
  SidebarNavLinkItem,
  SidebarNavSubLinkItem,
} from "@/navigation/types";
import type { Permission } from "@/types";

function flattenGroupChildren(
  children: SidebarNavGroupChild[],
  fallbackIcon?: SidebarNavLinkItem["icon"],
  fallbackPermission?: Permission,
): SidebarNavLinkItem[] {
  return children.flatMap((sub) => {
    if (sub.kind === "group") {
      return flattenGroupChildren(sub.items, sub.icon ?? fallbackIcon, sub.permission ?? fallbackPermission);
    }
    const icon = sub.icon ?? fallbackIcon;
    if (!icon) return [];
    return [
      {
        kind: "link" as const,
        to: sub.to,
        end: sub.end,
        labelKey: sub.labelKey,
        icon,
        permission: sub.permission ?? fallbackPermission,
      },
    ];
  });
}

/** Flatten top-level links and group children for mobile nav / search. */
export function flattenSidebarNavLinks(items: SidebarNavItem[]): SidebarNavLinkItem[] {
  return items.flatMap((item) => {
    if (item.kind === "link") return [item];
    if (item.kind === "group") {
      return flattenGroupChildren(item.items, item.icon, item.permission);
    }
    return [];
  });
}

export function filterSidebarSubLinks(
  items: SidebarNavSubLinkItem[],
  can: (permission: Permission) => boolean,
): SidebarNavSubLinkItem[] {
  return items.filter((item) => !item.permission || can(item.permission));
}

/** Filter group children (links and nested groups) by permission. */
export function filterSidebarGroupChildren(
  items: SidebarNavGroupChild[],
  can: (permission: Permission) => boolean,
): SidebarNavGroupChild[] {
  return items
    .map((item): SidebarNavGroupChild | null => {
      if (item.kind === "group") {
        if (item.permission && !can(item.permission)) return null;
        const nested = filterSidebarGroupChildren(item.items, can);
        if (nested.length === 0) return null;
        return { ...item, items: nested };
      }
      return !item.permission || can(item.permission) ? item : null;
    })
    .filter((item): item is SidebarNavGroupChild => item != null);
}

export function groupContainsPath(item: SidebarNavGroupItem, pathname: string): boolean {
  return item.items.some((sub) => {
    if (sub.kind === "group") return groupContainsPath(sub, pathname);
    return pathname === sub.to || pathname.startsWith(`${sub.to}/`);
  });
}

export function firstGroupLink(item: SidebarNavGroupItem): SidebarNavSubLinkItem | null {
  for (const sub of item.items) {
    if (sub.kind === "link") return sub;
    const nested = firstGroupLink(sub);
    if (nested) return nested;
  }
  return null;
}
